// useLessonSequence — the shared mount for the locked-sequence engine.
//
// BOTH the native (/student/lesson/:assignmentId) and LTI (/lti/scenes)
// surfaces call this hook (Phase 3 reuses it verbatim). It composes the pure
// engine (sequenceEngine) with the Supabase-direct persistence adapter
// (peProgress): on load it hydrates completion from the DB; after each step it
// persists and advances. The engine itself stays pure — all I/O lives here.

import { useCallback, useEffect, useRef, useState } from "react";
import {
  createSequence,
  completeStep as engineCompleteStep,
  view as engineView,
  type ResolvedChapterPayload,
  type SequenceState,
  type SequenceView,
  type StepId,
  type StudentAssetType,
  type Tier,
  type Lang,
} from "../engine/sequenceEngine";
import {
  fetchChapterPhases,
  fetchProgressPhaseIds,
  hydrateCompletedSteps,
  persistStepComplete,
  type ChapterPhase,
} from "../api/peProgress";

/** Full-chapter default asset set (Phase-4 picker will narrow this later). */
const FULL_CHAPTER_ASSETS: StudentAssetType[] = [
  "vocab_apply",
  "episode_video",
  "scene_reader",
  "formatives",
  "activity_booklet",
  "chapter_activity",
  "summative_quiz", // engine tier-gate drops this for T1/T2
];

export interface UseLessonSequenceArgs {
  chapterId: string; // pe_chapters PK, resolved upstream
  tier: Tier;
  lang: Lang;
  /** assignments.id (uuid) — the persistence key. Null disables persistence. */
  assignmentId: string | null;
  /** auth.users.id — the student. Null disables persistence. */
  studentId: string | null;
  /** Optional explicit asset selection (Phase-4 picker). Defaults to full chapter. */
  assets?: StudentAssetType[];
}

export interface UseLessonSequenceResult {
  loading: boolean;
  /** Construction error (e.g. empty sequence). Hydration failures degrade, not error. */
  error: string | null;
  view: SequenceView | null;
  /** Mark the current step complete, persist, and advance. */
  completeCurrentStep: () => Promise<void>;
  /** True while a completion is being persisted. */
  saving: boolean;
}

export function useLessonSequence(args: UseLessonSequenceArgs): UseLessonSequenceResult {
  const { chapterId, tier, lang, assignmentId, studentId } = args;
  const assets = args.assets ?? FULL_CHAPTER_ASSETS;

  const [state, setState] = useState<SequenceState | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Phase rows for this chapter, kept for completion persistence.
  const phasesRef = useRef<ChapterPhase[]>([]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    (async () => {
      // 1. Build the pure engine from the resolved payload.
      let base: SequenceState;
      try {
        const payload: ResolvedChapterPayload = { chapterId, tier, language: lang, assets };
        base = createSequence(payload);
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : "Could not build lesson sequence");
          setState(null);
          setLoading(false);
        }
        return;
      }

      // 2. Hydrate completion from the DB — FAIL-SOFT. Any persistence problem
      //    degrades to a fresh (unhydrated) sequence; the screen never crashes.
      let hydrated = base;
      if (assignmentId && studentId) {
        try {
          const phases = await fetchChapterPhases(chapterId);
          phasesRef.current = phases;
          const completedIds = await fetchProgressPhaseIds(assignmentId, studentId);
          const completedSteps = hydrateCompletedSteps(phases, completedIds);
          // Replay completions in canonical order; an out-of-order replay is
          // caught and the rest is skipped (degrade, don't crash).
          for (const step of completedSteps) {
            try {
              hydrated = engineCompleteStep(hydrated, step);
            } catch {
              console.warn(`[useLessonSequence] could not replay step "${step}" in order — stopping hydration here`);
              break;
            }
          }
        } catch (e) {
          console.warn("[useLessonSequence] hydration failed, starting fresh:", e);
        }
      }

      if (!cancelled) {
        setState(hydrated);
        setLoading(false);
      }
    })();

    return () => { cancelled = true; };
    // assets is derived from args; intentionally keyed on its identity-stable inputs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chapterId, tier, lang, assignmentId, studentId]);

  const completeCurrentStep = useCallback(async () => {
    if (!state) return;
    const v = engineView(state);
    const step: StepId | null = v.currentStep;
    if (!step) return;

    // Optimistically advance the pure state (engine enforces order).
    let next: SequenceState;
    try {
      next = engineCompleteStep(state, step);
    } catch (e) {
      console.warn("[useLessonSequence] completeStep rejected:", e);
      return;
    }
    setState(next);

    // Persist; on failure, log but keep the optimistic advance (re-sync on reload).
    if (assignmentId && studentId) {
      setSaving(true);
      try {
        await persistStepComplete({
          assignmentId,
          studentId,
          step,
          phases: phasesRef.current,
        });
      } catch (e) {
        console.warn("[useLessonSequence] persist failed (will re-sync on reload):", e);
      } finally {
        setSaving(false);
      }
    }
  }, [state, assignmentId, studentId]);

  return {
    loading,
    error,
    view: state ? engineView(state) : null,
    completeCurrentStep,
    saving,
  };
}
